import { Injectable } from '@angular/core';
import { ChatDTO } from 'src/app/DTO/Chat/ChatDTO';
import { ChatSessionDTO, ChatSessionListResponse, ChatMessageDTO } from 'src/app/pages/apps/chat/ChatSessionDTO';
import { AbstractService } from '../../AbstractService';
import { HttpClient, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, map, catchError, throwError } from 'rxjs';
import {environment} from "../../../../environments/environment";

// Interface for stream response data
export interface StreamResponse {
  type: 'text' | 'text_correction' | 'media' | 'done' | 'error' | 'search_results';
  text?: string;
  textCorrection?: string;
  removeText?: string;
  media?: {
    type: string;
    title: string;
    links: any[];
  };
  documents?: any[]; // Arama sonuçları için dokümanlar
  sessionId?: string;
  documentMetadata?: any;
  referencedDocuments?: string[];
  error?: string;
  done?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ChatService extends AbstractService<ChatDTO> {
  private messageStream = new Subject<StreamResponse>();

  constructor(protected override http: HttpClient) {
    super(http);
  }

  override getApiURL() {
    return 'chat';
  }

  /**
   * Get all chat sessions for the current user
   */
  getUserChatSessions(limit: number = 50, offset: number = 0): Observable<ChatSessionListResponse> {
    const url = `${environment.chatApiUrl}/${this.getApiURL()}/sessions`;
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    return this.http.get<ChatSessionListResponse>(url, { params }).pipe(
      map(response => {
        // Convert raw data to DTOs
        if (response && response.sessions) {
          response.sessions = response.sessions.map(session => {
            const sessionDTO = new ChatSessionDTO();
            sessionDTO.bindData(session);
            return sessionDTO;
          });
        }
        return response;
      }),
      catchError(error => {
        console.error('Error fetching chat sessions:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get messages for a specific chat session
   */
  getSessionMessages(sessionId: string): Observable<ChatMessageDTO[]> {
    const url = `${environment.chatApiUrl}/${this.getApiURL()}/sessions/${sessionId}/messages`;

    return this.http.get<any>(url).pipe(
      map(response => {
        if (response && response.messages) {
          const messages = response.messages.map((msg: any) => {
            const messageDTO = new ChatMessageDTO();
            messageDTO.bindData(msg);
            return messageDTO;
          });
          return messages;
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching session messages:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new chat session
   */
  createChatSession(title?: string): Observable<ChatSessionDTO> {
    const url = `${environment.chatApiUrl}/${this.getApiURL()}/sessions`;
    const data = title ? { title } : {};

    return this.http.post<any>(url, data).pipe(
      map(response => {
        const sessionDTO = new ChatSessionDTO();
        sessionDTO.bindData(response);
        return sessionDTO;
      }),
      catchError(error => {
        console.error('Error creating chat session:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Send a message to a specific chat session
   */
  sendSessionMessage(sessionId: string, content: string, model: string = 'gemini-1.5-flash'): Observable<ChatMessageDTO> {
    const url = `${environment.chatApiUrl}/${this.getApiURL()}/sessions/${sessionId}/messages`;
    const data = {
      content,
      model,
      stream: false // For non-streaming requests
    };

    return this.http.post<any>(url, data).pipe(
      map(response => {
        const messageDTO = new ChatMessageDTO();
        messageDTO.bindData(response);
        return messageDTO;
      }),
      catchError(error => {
        console.error('Error sending message:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a chat session
   */
  deleteChatSession(sessionId: string): Observable<any> {
    const url = `${environment.chatApiUrl}/${this.getApiURL()}/sessions/${sessionId}`;

    return this.http.delete(url).pipe(
      catchError(error => {
        console.error('Error deleting chat session:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Streams a chat message from the API with comprehensive response handling
   */
  streamChatMessage(selectedSessionId: string, prompt: string, model: string = 'gemini-1.5-flash', temperature: number = 0.7): Observable<StreamResponse> {
    // Get the authentication token
    const token = document.cookie.split('; ').find(row => row.startsWith('BILGEBOT_TOKEN='))?.split('=')[1];

    // Create headers for the request
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', `Bearer ${token ?? ''}`);

    // Get session ID if available
    let url = `${environment.chatApiUrl}/${this.getApiURL()}`;

    // Add parameters
    let params = new HttpParams()
      .set('prompt', prompt)
      .set('model', model)
      .set('temperature', temperature.toString())
      .set('stream', 'true');

    if (selectedSessionId) {
      params = params.set('sessionId', selectedSessionId);
    }

    // Create a new Subject for the message stream
    this.messageStream = new Subject<StreamResponse>();

    // Track partial messages that might be split across events
    let buffer = '';

    // Use HTTP request with text response type
    this.http.get(url, {
      headers,
      params,
      responseType: 'text',
      observe: 'events',
      reportProgress: true
    }).subscribe({
      next: (event) => {
        // Only process download progress events with text content
        if (event.type === HttpEventType.DownloadProgress && event['partialText']) {
          // Get the new chunk of text
          const newChunk = event['partialText'].substring(buffer.length);
          // Add the new chunk to our buffer
          buffer = event['partialText'];

          // Process any complete SSE messages in the buffer
          if (newChunk) {
            try {
              // Split by newlines to get individual SSE messages
              const lines = newChunk.split('\n');

              for (const line of lines) {
                // Check if this is a data line
                if (line.trim().startsWith('data:')) {
                  try {
                    // Extract the JSON data (remove 'data: ' prefix)
                    const dataStr = line.substring(5).trim();
                    if (dataStr) {
                      const jsonData = JSON.parse(dataStr);

                      // Handle different types of data from the stream
                      if (jsonData.error) {
                        // Error message
                        this.messageStream.next({
                          type: 'error',
                          error: jsonData.error
                        });
                      } else if (jsonData.documents) {
                        // Search results
                        this.messageStream.next({
                          type: 'search_results',
                          documents: jsonData.documents
                        });
                      } else if (jsonData.text_correction !== undefined) {
                        // Text correction
                        this.messageStream.next({
                          type: 'text_correction',
                          textCorrection: jsonData.text_correction,
                          removeText: jsonData.remove_text
                        });
                      } else if (jsonData.media) {
                        // Media/document links
                        this.messageStream.next({
                          type: 'media',
                          media: jsonData.media
                        });
                      } else if (jsonData.text !== undefined) {
                        // Regular text chunk
                        this.messageStream.next({
                          type: 'text',
                          text: jsonData.text
                        });
                      }

                      // Check if stream is done
                      if (jsonData.done) {
                        this.messageStream.next({
                          type: 'done',
                          done: true,
                          sessionId: jsonData.session_id,
                          documentMetadata: jsonData.document_metadata,
                          referencedDocuments: jsonData.referenced_documents
                        });
                        this.messageStream.complete();
                      }
                    }
                  } catch (parseError) {
                    console.error('Error parsing SSE JSON:', parseError, 'in line:', line);
                    this.messageStream.next({
                      type: 'error',
                      error: `Parse error: ${parseError.message}`
                    });
                  }
                }
              }
            } catch (e) {
              console.error('Error processing SSE data:', e);
              this.messageStream.next({
                type: 'error',
                error: `Processing error: ${e.message}`
              });
            }
          }
        }
      },
      error: (error) => {
        console.error('Error in SSE connection:', error);
        this.messageStream.next({
          type: 'error',
          error: `Connection error: ${error.message}`
        });
        this.messageStream.error(error);
      },
      complete: () => {
        // Make sure we complete the stream when the request is done
        this.messageStream.complete();
      }
    });

    // Timeout handling
    setTimeout(() => {
      if (!this.messageStream.closed) {
        this.messageStream.next({
          type: 'error',
          error: 'Request timeout - taking longer than expected'
        });
      }
    }, 30000); // Increased timeout to 30 seconds for document processing

    return this.messageStream.asObservable();
  }

  /**
   * Sends a chat message to the API (non-streaming)
   */
  sendChatMessage(prompt: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    // Add authorization header if token exists
    const token = document.cookie.split('; ').find(row => row.startsWith('BILGEBOT_TOKEN='))?.split('=')[1];
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.post(
      `${environment.chatApiUrl}/${this.getApiURL()}`,
      { prompt },
      { headers }
    );
  }
}
